/**
 * Narrowing a list of work down the client → application → project tree.
 *
 * ## Why this is a filter and not a cascade
 *
 * `components/board/PlacementSelect.tsx` walks the same three nouns, and is a
 * different thing. There you are *setting* where a task lives, so every level
 * is required and the one below is disabled until you answer. Here you are
 * *finding* a task that already exists, so every level is optional and skipping
 * all three is the common case - a contractor holds three tasks and should not
 * answer three questions to reach them. `docs/FINDINGS.md` F24.
 *
 * Pushing both behaviours into one component is how it grows two personalities,
 * so they stay apart deliberately.
 *
 * ## Why `undefined` and not `''`
 *
 * This shape is a subset of `BoardQuery`, so a scope can be spread straight
 * into `useTasks` and the narrowing happens in the query rather than over a
 * list already fetched - the same way the board's filter bar does it. An
 * absent key means "all"; `''` would be a filter matching nothing.
 */

/** The three levels above a task, as query parameters. Absent means "all". */
export interface TaskScope {
  clientId?: string;
  applicationId?: string;
  projectId?: string;
}

/** Outermost first. The order *is* the rule - everything after a level is below it. */
const LEVELS = ['clientId', 'applicationId', 'projectId'] as const;

export type ScopeLevel = (typeof LEVELS)[number];

/**
 * Set one level and drop everything under it.
 *
 * A project belongs to an application which belongs to a client, so keeping a
 * project after its client changes leaves a scope describing nothing. The
 * symptom of getting this wrong is not an error - the task list just goes
 * quietly empty, and it reads as "no work" rather than "bad filter".
 *
 * Clearing a level back to "all" drops the levels below for the same reason:
 * "all clients, but only this application" is not what the empty option means.
 *
 * `''` is what a `<select>` yields for its placeholder option, and is
 * normalised to absent here so no caller has to remember `|| undefined`.
 */
export function narrowScope(scope: TaskScope, level: ScopeLevel, id: string): TaskScope {
  const next: TaskScope = { ...scope, [level]: id || undefined };

  for (const below of LEVELS.slice(LEVELS.indexOf(level) + 1)) {
    next[below] = undefined;
  }

  return next;
}
